import React, { useCallback, useMemo } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { NavLink, useParams } from "react-router-dom";
import { isNil } from "ramda";

import { Layout } from "affinityUI/structures/layout";
import { Patient } from "components/views/patientDetails/patient";
import { useCreatePatientPdfMutation, useGetHospitalConfigQuery, useGetPatientResultsQuery } from "gql";
import { Restricted } from "lib/auth";
import { getNormalRanges, startDownload } from "lib/helpers";
import { usePreviousLocation } from "lib/previousLocation";

export const PatientPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { showBoundary: onError } = useErrorBoundary();

  const previousLocation = usePreviousLocation();
  const backLink =
    previousLocation?.pathname === "/patients/search"
      ? `${previousLocation?.pathname}${previousLocation?.search}`
      : "/patients";

  const { loading, data } = useGetPatientResultsQuery({ onError, variables: { id: id as string } });
  const patient = data?.patient;

  const { data: hospitalConfigData } = useGetHospitalConfigQuery();
  const hospitalConfig = hospitalConfigData?.hospitalConfig;
  const neutrophilThreshold = hospitalConfig?.neutrophilThreshold;
  const normalRanges = getNormalRanges(hospitalConfig);
  const [executeGetPdf] = useCreatePatientPdfMutation({ onError });

  const savePdf = useCallback(
    async (resultIds) => {
      const patientId = patient?.id;
      if (!isNil(patientId)) {
        return executeGetPdf({ variables: { patientId, results: resultIds, dryRun: false } }).then((res) => {
          const pdfUrl = res.data?.createPatientPdf?.pdfUrl;
          if (!isNil(pdfUrl)) {
            startDownload(pdfUrl);
          }
        });
      }
    },
    [executeGetPdf, patient?.id]
  );

  const title = patient ? `${patient.firstName} ${patient.lastName}` : "";
  const detailsActions = useMemo(
    () =>
      patient ? (
        <Restricted permits={["change_patient"]}>
          <NavLink data-testid="edit-patient" to="edit">
            Edit
          </NavLink>
        </Restricted>
      ) : null,
    [patient]
  );

  return (
    <Layout title={title} showBackBtn backLinkText="Back to patients" backLink={backLink} loading={loading}>
      {!loading && !isNil(patient) ? (
        <Patient
          normalRanges={normalRanges}
          neutrophilThreshold={neutrophilThreshold}
          patient={patient}
          detailsActions={detailsActions}
          savePdf={savePdf}
        />
      ) : (
        <></>
      )}
    </Layout>
  );
};
